import { MapPin, Shield, X } from 'lucide-react'

interface LocationPermissionModalProps {
  isOpen: boolean
  onClose: () => void
  onEnableLocation: () => void
}

export default function LocationPermissionModal({
  isOpen,
  onClose,
  onEnableLocation,
}: LocationPermissionModalProps) {
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
              <MapPin className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Location Required
          </h2>

          {/* Description */}
          <p className="text-center text-gray-600 mb-6">
            To like this post, please share your location.
          </p>

          {/* Privacy Explanation */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">
                  Your Privacy Matters
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  This enhances user privacy and content authenticity. We do <strong>not</strong> link your exact location to your account. Location is only used to verify you're in the same city as the content.
                </p>
              </div>
            </div>
          </div>

          {/* Why We Need This */}
          <div className="space-y-2 mb-6">
            <h3 className="font-semibold text-gray-900 text-sm">Why we need this:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <span>Verify authentic local engagement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <span>Prevent fake interactions from bots</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 font-bold">•</span>
                <span>Ensure you're engaging with local content</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={onEnableLocation}
              className="w-full py-3 px-6 rounded-full font-bold text-white transition-all"
              style={{ background: 'rgb(29, 155, 240)' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgb(26, 140, 216)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgb(29, 155, 240)'}
            >
              Enable Location
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 px-6 rounded-full font-bold text-gray-700 hover:bg-gray-100 transition-all"
            >
              Not Now
            </button>
          </div>

          {/* Footer Note */}
          <p className="text-xs text-center text-gray-500 mt-4">
            You can disable location sharing anytime in Settings
          </p>
        </div>
      </div>
    </>
  )
}
